import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formDataToSend = new URLSearchParams();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('message', formData.message);

    try {
      await fetch('https://readdy.ai/api/form/d5gdgijihlvellpk4odg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      setShowSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <WhatsAppButton />

      {showSuccess && (
        <div className="fixed top-20 right-3 left-3 sm:left-auto sm:right-4 z-50 bg-black text-white px-4 py-3 shadow-lg animate-fade-in rounded-lg">
          <div className="flex items-center space-x-3">
            <i className="ri-check-line text-xl text-[#F2A7C3]"></i>
            <span className="font-bold text-sm">{t('contact.successMessage')}</span>
          </div>
        </div>
      )}

      <div className="pt-20 md:pt-24 pb-12 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-4xl md:text-6xl font-black mb-3 tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {t('contact.title')}
            </h1>
            <p className="text-gray-600 text-sm">{t('contact.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6" data-readdy-form id="contact-form">
                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1.5 md:mb-2 tracking-wide">{t('contact.fullName')} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1.5 md:mb-2 tracking-wide">{t('contact.email')} *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1.5 md:mb-2 tracking-wide">{t('contact.phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+216"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1.5 md:mb-2 tracking-wide">{t('contact.message')} *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-gray-50 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black text-white py-3.5 md:py-4 text-sm font-bold tracking-widest hover:bg-gray-800 transition-colors disabled:bg-gray-400 cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? '...' : t('contact.send')}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-4 md:space-y-6">
              <div className="bg-gray-50 p-5 md:p-8">
                <h2 className="text-xl md:text-2xl font-black mb-4 md:mb-6 tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  {t('contact.information')}
                </h2>
                
                <div className="space-y-4 md:space-y-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-lg md:text-xl"></i>
                    </div>
                    <div>
                      <p className="font-bold text-sm mb-0.5">{t('contact.phoneLabel')}</p>
                      <p className="text-sm text-gray-600">+216 XX XXX XXX</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg md:text-xl"></i>
                    </div>
                    <div>
                      <p className="font-bold text-sm mb-0.5">{t('contact.emailLabel')}</p>
                      <p className="text-sm text-gray-600 break-all">contact@ptitvoyou.tn</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-lg md:text-xl"></i>
                    </div>
                    <div>
                      <p className="font-bold text-sm mb-0.5">{t('contact.hours')}</p>
                      <p className="text-sm text-gray-600">{t('contact.hoursText').split('\n')[0]}</p>
                      <p className="text-sm text-gray-600">{t('contact.hoursText').split('\n')[1]}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-black text-white p-5 md:p-8">
                <h3 className="text-lg md:text-xl font-black mb-3 tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  {t('contact.followUs')}
                </h3>
                <p className="text-sm mb-4 text-gray-300">
                  {t('contact.communityText')}
                </p>
                <div className="flex space-x-4">
                  <a href="https://www.instagram.com/ptitvoyoureveur?igsh=NG5mMzF4N2JxY2xm&utm_source=qr" target="_blank" rel="nofollow noreferrer" className="w-11 h-11 bg-white text-black rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}